// src/pages/FundPlanner/components/FundStructurer/index.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import dayjs from "lib/dayjs";

import Page from "components/Page";
import TopTabMenu from "components/TopTabMenu";
import FileRibbon from "components/FileManager/FileRibbon";
import FileManagerModal from "components/FileManager/FileManagerModal";
import FileManagerPanel from "components/FileManager/FileManagerPanel";
import ModalBackdrop from "components/ModalBackdrop";

import FundSetup from "./FundSetup";
import DrawSchedule from "./CashflowSchedule";
import InvestmentSolution from "./InvestmentSolution";
import CashflowSufficiency from "./CashflowSufficiency";
import FundStats from "./FundStats";
import UsTreasuryStats from "./UsTreasuryStats";
import CashflowWizard from "./CashflowWizard";
import {
  buildStructurerReportData,
  buildStructurerSufficiencyRows,
} from "pages/FundPlanner/reports/structurerReportData";
import exportStructurerPdf from "pages/FundPlanner/reports/exportStructurerPdf";

import { useFundPlanner } from "pages/FundPlanner/context/StructurerContext";
import { useProfile } from "context/ProfileContext";
import { useDialog } from "context/DialogContext";
import {
  useFundsFileManager,
  fundsOpenView,
} from "pages/FundPlanner/components/Structurer/useFileManager";

import { supabase } from "services/supabaseClient";
import { fetchMarketRates } from "pages/FundPlanner/lib/fetchMarketRates";
import { solveFundingStructure } from "pages/FundPlanner/lib/solverMain";
import {
  calculateHypotheticalPortfolio,
  HYPOTHETICAL_CURVE_UNIVERSES,
  normalizeHypotheticalPortfolioResult,
} from "pages/FundPlanner/lib/usTreasuryCalculator";

import { useLocation, useNavigate } from "react-router-dom";
import { getSharingPermissions, normalizeSharing } from "lib/permissions";

function num(x, dflt = null) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

function isRefundingDepositFundType(ft) {
  const s = String(ft || "").trim().toLowerCase();
  // flexible matching in case labels evolve
  return (
    s === "refunding deposit" || (s.includes("refunding") && s.includes("deposit"))
  );
}

/* ------------------------------------------------------------------
 * Draw schedule semantic no-op guard (breaks ExcelGrid loop)
 * ------------------------------------------------------------------ */
function normDate(v) {
  return String(v ?? "").trim();
}
function normAmt(v) {
  if (v == null) return "";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "";
  const s = String(v).trim();
  if (!s) return "";
  const cleaned = s.replace(/,/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? String(n) : s;
}
function isBlankSchedule(rows) {
  const a = Array.isArray(rows) ? rows : [];
  if (a.length === 0) return true;
  if (a.length !== 1) return false;
  const r = a[0] || {};
  return !normDate(r.drawDate) && !normAmt(r.drawRequirement);
}
function hasCashflowSchedule(rows) {
  const a = Array.isArray(rows) ? rows : [];

  return a.some((r) => {
    const d = normDate(r?.drawDate);
    const amt = normAmt(r?.drawRequirement);
    return d || amt;
  });
}
function schedulesEquivalent(a, b) {
  const A = Array.isArray(a) ? a : [];
  const B = Array.isArray(b) ? b : [];

  // Treat "empty/effectively blank" as equivalent
  if (isBlankSchedule(A) && isBlankSchedule(B)) return true;

  if (A.length !== B.length) return false;

  for (let i = 0; i < A.length; i++) {
    if (normDate(A[i]?.drawDate) !== normDate(B[i]?.drawDate)) return false;
    if (normAmt(A[i]?.drawRequirement) !== normAmt(B[i]?.drawRequirement))
      return false;
  }
  return true;
}

function inferSavedInvestmentUniverse(form = {}, solution = {}) {
  const saved = solution && typeof solution === "object" ? solution : {};
  if (saved.customCurve || saved.methodology === "custom-curve") return "custom";
  if (saved.usAgency || saved.methodology === "us-agencies") return "agency";
  if (saved.usTreasury || saved.methodology === "us-treasury") return "ust";
  if (
    Object.prototype.hasOwnProperty.call(saved, "investments") ||
    Object.prototype.hasOwnProperty.call(saved, "inflows")
  ) {
    return "slgs";
  }

  const configured = String(form?.selectedCurve || "").trim().toLowerCase();
  return ["slgs", "ust", "agency", "custom"].includes(configured)
    ? configured
    : "slgs";
}

function savedCurveResult(solution = {}, universe = "slgs") {
  if (!solution || typeof solution !== "object") return null;
  if (universe === "ust") return solution.usTreasury || null;
  if (universe === "agency") return solution.usAgency || null;
  if (universe === "custom") return solution.customCurve || null;
  return null;
}

export default function FundStructurer() {
  const location = useLocation();
  const navigate = useNavigate();

  /* ------------------------------------------------------------------
   * Profile / org context (JWT-authoritative)
   * ------------------------------------------------------------------ */
  const {
    profile,
    loading: profileLoading,
    availableOrgs,
    selectedOrg,
    orgMode,
    selectOrg,
  } = useProfile();

  const tenantId = selectedOrg?.tenant_id ?? profile?.tenant_id ?? null;
  const orgId = selectedOrg?.id ?? null;

  /* ------------------------------------------------------------------
   * Dialogs (global, via provider)
   * ------------------------------------------------------------------ */
  const { showDialog } = useDialog();

  /* ------------------------------------------------------------------
   * FundPlanner state
   * ------------------------------------------------------------------ */
  const {
    fundType,
    setFundType,
    sharing,
    setSharing,
    fundMode,
    setFundMode,
    selectedCurve,
    setSelectedCurve,
    settlementDate,
    setSettlementDate,

    asOfDate,
    setAsOfDate,
    totalAmount,
    setTotalAmount,
    fundName,
    setFundName,
    linkedDealId,
    setLinkedDealId,

    drawSchedule,
    setDrawSchedule,
    investmentSolution,
    setInvestmentSolution,
    sufficiencyTable,
    setSufficiencyTable,

    curveData,
    setCurveData,
    customCurveRows,
    setCustomCurveRows,
    userCurveDate,
    setUserCurveDate,
    userCurveDescription,
    setUserCurveDescription,

    isSolving,
    setIsSolving,
    isDirty,
    setIsDirty,
    suppressDirtyRef,

    resetPlanner,

    currentId,
    setCurrentId,
    currentName,
    setCurrentName,
    createdBy,
    setCreatedBy,
    currentOwner,
    activeTab,
    setActiveTab,
    showWizard,
    setShowWizard,
  } = useFundPlanner();

  const permissions = getSharingPermissions(
    { id: currentId, created_by: createdBy, sharing },
    profile
  );
  const { canEdit, canDelete, canChangeSharing, isReadOnly } = permissions;

  /* ------------------------------------------------------------------
   * File manager (Funds)
   * ------------------------------------------------------------------ */
  const fm = useFundsFileManager({ limit: 100 });
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const today = dayjs().format("YYYY-MM-DD");

  /* ------------------------------------------------------------------
   * Status
   * ------------------------------------------------------------------ */
  const status = isSaving
    ? "saving"
    : !currentId
      ? "unsaved"
      : isDirty
        ? "modified"
        : "saved";
  const hypotheticalConfig = HYPOTHETICAL_CURVE_UNIVERSES[selectedCurve] || null;
  const hypotheticalResult = hypotheticalConfig
    ? normalizeHypotheticalPortfolioResult(
        investmentSolution?.[hypotheticalConfig.resultKey],
        selectedCurve
      )
    : null;

  const structurerReport = useMemo(
    () =>
      buildStructurerReportData({
        orgName: selectedOrg?.name || selectedOrg?.legal_name || "",
        fundName: fundName || currentName,
        fundType,
        fundMode,
        settlementDate,
        totalAmount,
        selectedCurve,
        asOfDate: selectedCurve === "custom" ? userCurveDate : asOfDate,
        draws: drawSchedule,
        investments: investmentSolution.investments || [],
        inflows: investmentSolution.inflows || [],
        hypotheticalPortfolio: hypotheticalResult,
      }),
    [
      asOfDate,
      currentName,
      drawSchedule,
      fundMode,
      fundName,
      fundType,
      investmentSolution.inflows,
      investmentSolution.investments,
      hypotheticalResult,
      selectedCurve,
      userCurveDate,
      selectedOrg?.legal_name,
      selectedOrg?.name,
      settlementDate,
      totalAmount,
    ]
  );

  const handleExportPdf = useCallback(async () => {
    if (!structurerReport.hasResults || isPdfGenerating) return;
    setIsPdfGenerating(true);
    try {
      await exportStructurerPdf({ report: structurerReport, orgId: selectedOrg?.id });
    } catch (err) {
      console.warn("FP Structurer PDF export failed:", err);
      await showDialog({
        title: "PDF export failed",
        message: err?.message || "Failed to export the FP Structurer report.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
    } finally {
      setIsPdfGenerating(false);
    }
  }, [isPdfGenerating, selectedOrg?.id, showDialog, structurerReport]);

  /* ------------------------------------------------------------------
   * Identity
   * ------------------------------------------------------------------ */
  const totalAmountLocked = useMemo(
    () => hasCashflowSchedule(drawSchedule),
    [drawSchedule]
  );

  /**
   * IMPORTANT:
   * - Must be stable (no drawSchedule dependency) or ExcelGrid can get into a
   *   prop-change → onChange → setState loop.
   * - Must resolve against REAL prev state (functional update).
   * - Must ignore semantic no-ops to break loops even if ExcelGrid spams onChange.
   */
  const setDrawScheduleDbg = useCallback(
    (next, _meta = {}) => {
      setDrawSchedule((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        const nextResolved = Array.isArray(resolved) ? resolved : [];

        // Break infinite loops caused by "no-op" updates, often blank row
        if (schedulesEquivalent(prev, nextResolved)) {
          return prev;
        }

        return nextResolved;
      });
    },
    [setDrawSchedule]
  );

  const setDrawScheduleFromGrid = useCallback(
    (next) => setDrawScheduleDbg(next, { reason: "DrawSchedule.onChange" }),
    [setDrawScheduleDbg]
  );

  const setDrawScheduleFromWizard = useCallback(
    (next) => setDrawScheduleDbg(next, { reason: "wizard.generate" }),
    [setDrawScheduleDbg]
  );

  const setDrawScheduleFromLoad = useCallback(
    (next) => setDrawScheduleDbg(next, { reason: "load.record" }),
    [setDrawScheduleDbg]
  );

  const setDrawScheduleFromHandoffSeed = useCallback(
    (next) =>
      setDrawScheduleDbg(next, { reason: "handoff.seedNewUnsavedFromIssue" }),
    [setDrawScheduleDbg]
  );

  /* ------------------------------------------------------------------
   * Refunding Deposit: always force first Requirement = Total Amount ($)
   * ------------------------------------------------------------------ */
  const isRefundingDeposit = isRefundingDepositFundType(fundType);

  const marketSigRef = useRef("");

  useEffect(() => {
    if (!isRefundingDeposit) return;

    const gross = num(totalAmount, 0) || 0;
    const desired = gross > 0 ? gross : "";

    setDrawScheduleDbg(
      (prev) => {
        const rows = Array.isArray(prev) ? prev : [];

        // Ensure at least one row exists
        if (rows.length === 0) {
          return [
            {
              id: Date.now(),
              drawDate: "",
              drawRequirement: desired,
            },
          ];
        }

        // Force first row requirement to match totalAmount
        const r0 = rows[0] || {};
        const next0 = { ...r0, drawRequirement: desired };

        // Preserve other rows exactly as-is
        return [next0, ...rows.slice(1)];
      },
      { reason: "refundingDeposit.syncFirstRequirementToTotalAmount" }
    );
  }, [isRefundingDeposit, totalAmount, setDrawScheduleDbg]);

  /* ------------------------------------------------------------------
   * Max draw date (primitive)
   * ------------------------------------------------------------------ */
  const maxDrawDate = useMemo(() => {
    const rows = Array.isArray(drawSchedule) ? drawSchedule : [];
    const validDates = rows
      .map((r) => dayjs(r?.drawDate))
      .filter((d) => d.isValid());

    if (validDates.length === 0) return null;

    return validDates
      .reduce((max, d) => (d.isAfter(max) ? d : max), validDates[0])
      .format("YYYY-MM-DD");
  }, [drawSchedule]);

  /* ------------------------------------------------------------------
   * Load market rates (latest-wins, no lockout)
   * ------------------------------------------------------------------ */
  const ratesReqIdRef = useRef(0);

  useEffect(() => {
    if (selectedCurve === "custom") {
      setCurveData(Array.isArray(customCurveRows) ? customCurveRows : []);
      return;
    }

    // If inputs aren’t ready, clear curve to prevent stale usage
    if (!orgId || !maxDrawDate || !asOfDate) {
      if (!suppressDirtyRef.current) setCurveData([]);
      return;
    }

    const reqId = ++ratesReqIdRef.current;
    let cancelled = false;

    const loadRates = async () => {
      try {
        const data = await fetchMarketRates({
          securityType: selectedCurve || "slgs",
          asOfDate,
          maxMaturityDate: maxDrawDate,
        });

        // Apply only if this is still the latest request
        if (cancelled) return;
        if (reqId !== ratesReqIdRef.current) return;

        setCurveData(Array.isArray(data) ? data : []);
      } catch (e) {
        if (cancelled) return;
        if (reqId !== ratesReqIdRef.current) return;

        console.error("❌ [FS] loadRates failed:", e);
        setCurveData([]);
      }
    };

    loadRates();

    return () => {
      cancelled = true;
    };
  }, [
    orgId,
    selectedCurve,
    asOfDate,
    maxDrawDate,
    customCurveRows,
    setCurveData,
    suppressDirtyRef,
  ]);

  useEffect(() => {
    const curve = String(selectedCurve || "");
    const date = String(asOfDate || "");
    const sig = `${curve}__${date}`;

    // First meaningful run: initialize signature, do not clear anything.
    if (!marketSigRef.current) {
      marketSigRef.current = sig;
      return;
    }

    // No change
    if (marketSigRef.current === sig) return;

    // Update signature first so we do not re-process the same change.
    marketSigRef.current = sig;

    // If we're in the middle of a programmatic update (load/reset/solver write),
    // don't invalidate.
    if (suppressDirtyRef.current) return;

    // Invalidate outputs because they are tied to prior market inputs.
    suppressDirtyRef.current = true;
    setInvestmentSolution({});
    setSufficiencyTable([]);
    suppressDirtyRef.current = false;
  }, [
    asOfDate,
    selectedCurve,
    setInvestmentSolution,
    setSufficiencyTable,
    suppressDirtyRef,
  ]);

  /* ------------------------------------------------------------------
   * Wizard
   * ------------------------------------------------------------------ */
  const handleWizardGenerate = (generated) => {
    setDrawScheduleFromWizard(generated);
    setInvestmentSolution({});
    setSufficiencyTable([]);
    setShowWizard(false);
    setActiveTab("Cash Flow Schedule");
  };

  const handleWizardCancel = () => {
    setShowWizard(false);
    setActiveTab("Cash Flow Schedule");
  };

  /* ------------------------------------------------------------------
   * Solver
   * ------------------------------------------------------------------ */
  const handleSolve = async () => {
    if (isReadOnly) {
      await showDialog({
        title: "Read-Only Fund",
        message: "This fund is read-only. Use Save As to create an editable copy.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }
    if (!drawSchedule.length) {
      await showDialog({
        title: "FP Structurer",
        message: "No draw schedule available.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    if (hypotheticalConfig && !settlementDate) {
      await showDialog({
        title: "FP Structurer",
        message: "Please enter a Settlement Date.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    if (selectedCurve !== "custom" && !asOfDate) {
      await showDialog({
        title: "FP Structurer",
        message: "Please select a Market as of date.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    if (
      selectedCurve !== "custom" &&
      (!Array.isArray(curveData) || curveData.length === 0)
    ) {
      await showDialog({
        title: "FP Structurer",
        message: "No rate data available.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    setIsSolving(true);
    await new Promise((r) => setTimeout(r, 50));

    try {
      const draws = drawSchedule.map((r) => ({
        id: r.id,
        date: r.drawDate,
        amount: r.drawRequirement,
      }));

      if (hypotheticalConfig) {
        const hypotheticalPortfolio = calculateHypotheticalPortfolio({
          universe: selectedCurve,
          asOfDate: selectedCurve === "custom" ? userCurveDate : asOfDate,
          settlementDate,
          cashFlows: draws,
          rates: curveData,
        });
        if (selectedCurve === "custom") {
          hypotheticalPortfolio.curveAsOfDate = userCurveDate || null;
          hypotheticalPortfolio.curveDescription = userCurveDescription;
        }

        suppressDirtyRef.current = true;
        setInvestmentSolution({
          [hypotheticalConfig.resultKey]: hypotheticalPortfolio,
        });
        setSufficiencyTable([]);
        suppressDirtyRef.current = false;
        setIsDirty(true);
        setActiveTab("Investment Solution");
        return;
      }

      const { investments, inflows } = await solveFundingStructure({
        mode: fundMode,
        draws,
        rates: curveData,
        securityType: selectedCurve,
        asOfDate,
        settlementDate,
        dayCount: "act/365",
      });

      suppressDirtyRef.current = true;
      setInvestmentSolution({ investments, inflows });
      suppressDirtyRef.current = false;

      const suff = buildStructurerSufficiencyRows({
        draws: drawSchedule,
        investments,
        inflows,
        fundMode,
      });
      suppressDirtyRef.current = true;
      setSufficiencyTable(suff);
      suppressDirtyRef.current = false;

      setIsDirty(true);
      setActiveTab("Investment Solution");

      setActiveTab("Investment Solution");
    } catch (err) {
      console.error("Solver error:", err);
      setInvestmentSolution({});
      setSufficiencyTable([]);
      await showDialog({
        title: hypotheticalConfig
          ? `${hypotheticalConfig.universeLabel} calculation`
          : "FP Structurer",
        message: err?.message || "The calculation could not be completed.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
    } finally {
      setIsSolving(false);
    }
  };

  /* ------------------------------------------------------------------
   * Load fund
   * ------------------------------------------------------------------ */
  const handleLoad = useCallback(
    async (record) => {
      if (!record) return;

      let full = record;

      // File-manager rows and recents may contain stale or partial JSON fields.
      // Always hydrate the selected record from the authoritative database row.
      if (record?.id) {
        try {
          const { data, error } = await supabase
            .from("fp_funds")
            .select(
              `
                id,
                org_id,
                tenant_id,
                name,
                created_by,
                updated_at,
                form,
                draw_schedule,
                solution,
                linked_ds_deal_id,
                fund_type,
                sharing
              `
            )
            .eq("id", record.id)
            .single();

          if (error) throw error;
          full = data;
        } catch (e) {
          console.error("❌ [FS] handleLoad hydration failed:", e);
          full = record;
        }
      }

      suppressDirtyRef.current = true;

      const f = full.form || {};

      const dbFundType =
        typeof full.fund_type === "string" ? full.fund_type : null;
      const dbLinkedDealId = full.linked_ds_deal_id ?? null;

      const nextFundType = dbFundType || f.fundType || "project";
      const nextLinkedDealId = dbLinkedDealId || f.linkedDealId || null;

      const dbName = typeof full.name === "string" ? full.name : "";
      const nextFundName = nextLinkedDealId ? "" : dbName || f.fundName || "";

      const nextFundMode = f.fundMode || "gross";
      let nextSolution = full.solution || {};
      const nextCurve = inferSavedInvestmentUniverse(f, nextSolution);
      const storedCurveResult = savedCurveResult(nextSolution, nextCurve);
      const savedUserCurve = f.userCurve && typeof f.userCurve === "object"
        ? f.userCurve
        : null;
      const nextCustomCurveRows = Array.isArray(savedUserCurve?.rows)
        ? savedUserCurve.rows
        : Array.isArray(f.customCurveRows) ? f.customCurveRows : [];
      const nextUserCurveDate =
        savedUserCurve?.date || storedCurveResult?.curveAsOfDate || "";
      const nextUserCurveDescription = savedUserCurve?.description || "";
      let nextSettlement = f.settlementDate || today;
      const nextAsOf =
        f.asOfDate ||
        (nextCurve === "ust" || nextCurve === "agency"
          ? storedCurveResult?.curveAsOfDate || storedCurveResult?.valuationDate || ""
          : "");
      const nextTotal = f.totalAmount || 100_000_000;
      let didReconcileLinkedDate = false;

      if (nextLinkedDealId) {
        try {
          const { data: linkedIssue, error: linkedIssueErr } = await supabase
            .from("ds_deals")
            .select("id,delivery_date")
            .eq("id", nextLinkedDealId)
            .maybeSingle();

          if (linkedIssueErr) throw linkedIssueErr;

          const dsDeliveryDate = linkedIssue?.delivery_date || null;

          if (
            dsDeliveryDate &&
            nextSettlement &&
            dsDeliveryDate !== nextSettlement
          ) {
            const res = await showDialog({
              title: "Linked Issue Date Changed",
              message:
                `The linked DebtStudio Issue delivery date has changed from ${nextSettlement} to ${dsDeliveryDate}. ` +
                "Update and clear solution?",
              buttons: [
                { label: "Keep Current Date", value: "keep", variant: "ghost" },
                { label: "Update Date", value: "update", variant: "primary" },
              ],
              defaultAction: "update",
            });

            if (res === "update") {
              nextSettlement = dsDeliveryDate;
              nextSolution = {};
              didReconcileLinkedDate = true;

              full = {
                ...full,
                form: {
                  ...f,
                  settlementDate: dsDeliveryDate,
                },
                solution: {},
              };
            }
          }
        } catch (e) {
          console.error("❌ [FS] linked issue date check failed:", e);
        }
      }

      setFundType(nextFundType);
      setSharing(normalizeSharing(full.sharing));
      setFundMode(nextFundMode);
      setSelectedCurve(nextCurve);
      setCustomCurveRows(nextCustomCurveRows);
      setUserCurveDate(nextUserCurveDate);
      setUserCurveDescription(nextUserCurveDescription);
      setSettlementDate(nextSettlement);
      setAsOfDate(nextAsOf);
      setTotalAmount(nextTotal);
      setLinkedDealId(nextLinkedDealId);
      setFundName(nextFundName);
      setDrawScheduleFromLoad(full.draw_schedule || []);
      setInvestmentSolution(nextSolution);
      setSufficiencyTable([]);

      marketSigRef.current = `${String(nextCurve || "")}__${String(
        nextAsOf || ""
      )}`;

      setCurrentId(full.id);
      setCreatedBy(full.created_by || null);

      const uiName =
        (typeof full.name === "string" && full.name.trim()) ||
        (nextLinkedDealId ? "Linked Fund" : "(Untitled Fund)");
      setCurrentName(uiName);

      setIsDirty(didReconcileLinkedDate);

      fm.openRecord(full);

      setTimeout(() => {
        suppressDirtyRef.current = false;
      }, 0);
    },
    [
      fm,
      setAsOfDate,
      setCreatedBy,
      setCurrentId,
      setCurrentName,
      setFundMode,
      setFundName,
      setFundType,
      setSharing,
      setInvestmentSolution,
      setIsDirty,
      setLinkedDealId,
      setSelectedCurve,
      setCustomCurveRows,
      setUserCurveDate,
      setUserCurveDescription,
      setSettlementDate,
      setSufficiencyTable,
      setTotalAmount,
      showDialog,
      suppressDirtyRef,
      today,
      setDrawScheduleFromLoad,
    ]
  );

  /* ------------------------------------------------------------------
   * New / Save / Save As / Delete
   * ------------------------------------------------------------------ */
  const handleNewFund = () => {
    resetPlanner();
    suppressDirtyRef.current = true;

    setLinkedDealId(null);
    setFundName("");

    suppressDirtyRef.current = false;

    setCurrentId(null);
    setCurrentName(`New Fund ${new Date().toLocaleDateString()}`);
    setActiveTab("Main");
    setIsDirty(false);
  };

  const saveFund = async () => {
    if (!selectedOrg?.id || !tenantId || !profile?.id) {
      await showDialog({
        title: "FP Structurer",
        message: "Missing organization, tenant, or user context.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    if (currentId && !canEdit) {
      await showDialog({
        title: "Read-Only Fund",
        message: "This fund is read-only. Use Save As to create an editable copy.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    const isLinked = !!linkedDealId;

    if (!isLinked && !fundName?.trim()) {
      await showDialog({
        title: "FP Structurer",
        message: "Please enter a Fund Name.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    if (isLinked && !linkedDealId) {
      await showDialog({
        title: "FP Structurer",
        message: "This fund is marked as linked, but no Issue is linked.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    const normalizedSharing = normalizeSharing(sharing);
    const savedUniverse = inferSavedInvestmentUniverse(
      { selectedCurve },
      investmentSolution
    );

    const form = {
      fundType,
      fundMode,
      selectedCurve: savedUniverse,
      settlementDate,
      asOfDate,
      totalAmount,
      fundName: isLinked ? "" : (fundName || "").trim(),
      linkedDealId: isLinked ? linkedDealId : null,
      userCurve: {
        date: userCurveDate,
        description: userCurveDescription,
        rows: customCurveRows,
      },
    };

    const payload = {
      org_id: selectedOrg.id,
      tenant_id: tenantId,
      created_by: profile.id,
      name: isLinked ? null : (fundName || "").trim(),
      linked_ds_deal_id: isLinked ? linkedDealId : null,
      fund_type: fundType || "project",
      sharing: normalizedSharing,
      form,
      draw_schedule: drawSchedule,
      solution: investmentSolution,
    };

    if (!currentId) {
      const { data, error } = await supabase
        .from("fp_funds")
        .insert(payload)
        .select(
          `
            id,
            org_id,
            tenant_id,
            name,
            created_by,
            updated_at,
            form,
            draw_schedule,
            solution,
            linked_ds_deal_id,
            fund_type,
            sharing
          `
        )
        .single();

      if (error) {
        await showDialog({
          title: "FP Structurer",
          message: error.message,
          buttons: [{ label: "OK", value: "ok", variant: "primary" }],
          defaultAction: "ok",
        });
        return;
      }

      setCurrentId(data.id);
      setCreatedBy(data.created_by || profile.id);

      const uiName =
        (typeof data.name === "string" && data.name.trim()) ||
        (data.linked_ds_deal_id ? "Linked Fund" : "(Untitled Fund)");
      setCurrentName(uiName);

      setIsDirty(false);
      fm.refresh();
      fm.openRecord(data);
    } else {
      const updatePayload = {
        name: payload.name,
        linked_ds_deal_id: payload.linked_ds_deal_id,
        fund_type: payload.fund_type,
        sharing: normalizedSharing,
        form: payload.form,
        draw_schedule: payload.draw_schedule,
        solution: payload.solution,
      };
      const { data, error } = await supabase
        .from("fp_funds")
        .update(updatePayload)
        .eq("id", currentId)
        .select("id,form,solution,updated_at")
        .single();

      if (error) {
        await showDialog({
          title: "FP Structurer",
          message: error.message,
          buttons: [{ label: "OK", value: "ok", variant: "primary" }],
          defaultAction: "ok",
        });
        return;
      }

      const persistedUniverse = inferSavedInvestmentUniverse(
        data?.form || {},
        data?.solution || {}
      );
      if (persistedUniverse !== savedUniverse) {
        await showDialog({
          title: "FP Structurer",
          message: "The saved Investment Universe did not match the saved solution.",
          buttons: [{ label: "OK", value: "ok", variant: "primary" }],
          defaultAction: "ok",
        });
        return;
      }
      if (selectedCurve !== savedUniverse) {
        suppressDirtyRef.current = true;
        setSelectedCurve(savedUniverse);
        suppressDirtyRef.current = false;
      }

      const uiName = isLinked
        ? "Linked Fund"
        : (fundName || "").trim() || "(Untitled Fund)";
      setCurrentName(uiName);

      setIsDirty(false);
      fm.refresh();
    }

  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      return await saveFund();
    } finally {
      setIsSaving(false);
    }
  };

    const handleSaveAs = async () => {
      if (!selectedOrg?.id || !tenantId || !profile?.id) {
        await showDialog({
          title: "FP Structurer",
          message: "Missing organization, tenant, or user context.",
          buttons: [{ label: "OK", value: "ok", variant: "primary" }],
          defaultAction: "ok",
        });
        return;
      }

    const baseName = (fundName || "").trim() || "Fund";
    const defaultName = `${baseName} (Copy)`;

    const res = await showDialog({
      title: "Save Fund As",
      message: "Enter a name for the new fund.",
      input: {
        label: "Fund name",
        defaultValue: defaultName,
        placeholder: "Fund name",
        helpText:
          "A new unlinked fund will be created with the same inputs and solution.",
        ariaLabel: "Save fund as name",
      },
      buttons: [
        { label: "Cancel", value: "cancel", variant: "ghost" },
        { label: "Save", value: "yes", variant: "primary" },
      ],
      defaultAction: "yes",
    });

    const action = typeof res === "string" ? res : res?.value;
    const newNameRaw = typeof res === "string" ? "" : res?.inputValue;

    if (action !== "yes") return;

    const newName = String(newNameRaw || "").trim();
    if (!newName) {
      await showDialog({
        title: "Save Fund As",
        message: "Please enter a fund name.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    const savedUniverse = inferSavedInvestmentUniverse(
      { selectedCurve },
      investmentSolution
    );

    const nextForm = {
      fundType,
      fundMode,
      selectedCurve: savedUniverse,
      settlementDate,
      asOfDate,
      totalAmount,
      fundName: newName,
      linkedDealId: null,
      userCurve: {
        date: userCurveDate,
        description: userCurveDescription,
        rows: customCurveRows,
      },
    };

    const { data, error } = await supabase
      .from("fp_funds")
      .insert({
        org_id: selectedOrg.id,
        tenant_id: tenantId,
        created_by: profile.id,
        name: newName,
        linked_ds_deal_id: null,
        fund_type: fundType || "project",
        sharing: "user",
        form: nextForm,
        draw_schedule: drawSchedule,
        solution: investmentSolution,
      })
      .select(
        `
          id,
          org_id,
          tenant_id,
          name,
          created_by,
          updated_at,
          form,
          draw_schedule,
          solution,
          linked_ds_deal_id,
          fund_type,
          sharing
        `
      )
      .single();

    if (error) {
      await showDialog({
        title: "FP Structurer",
        message: error.message,
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    suppressDirtyRef.current = true;

    setCurrentId(data.id);
    setCreatedBy(data.created_by || profile.id);
    setSharing("user");
    setCurrentName(data.name || newName);

    setLinkedDealId(null);
    setFundName(newName);

    suppressDirtyRef.current = false;

    setIsDirty(false);
    fm.refresh();
    fm.openRecord(data);
  };

  const handleDeleteFund = async () => {
    if (!currentId) return;

    const record = fm.records.find((r) => r.id === currentId);
    if (!record || record.created_by !== profile?.id) {
      await showDialog({
        title: "FP Structurer",
        message: "You can only delete funds you created.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    const res = await showDialog({
      title: "Delete Fund",
      message: "Delete this fund? This cannot be undone.",
      buttons: [
        { label: "Cancel", value: "cancel", variant: "ghost" },
        { label: "Delete", value: "yes", variant: "primary" },
      ],
      defaultAction: "yes",
    });

    if (res !== "yes") return;

    try {
      await fm.deleteRecord(currentId);
      resetPlanner();
      setCurrentId(null);
      setCurrentName(null);
      setIsDirty(false);

      await showDialog({
        title: "FP Structurer",
        message: "Fund deleted.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
    } catch (err) {
      await showDialog({
        title: "FP Structurer",
        message: err?.message || "Failed to delete fund.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
    }
  };

  /* ------------------------------------------------------------------
   * DS → FundPlanner handoff (router state)
   * ------------------------------------------------------------------ */
  const handoffRef = useRef(null);

  useEffect(() => {
    const payload = location?.state?.dsToFundPlanner || null;
    if (!payload) return;

    const sig = JSON.stringify(payload);
    if (handoffRef.current === sig) return;
    handoffRef.current = sig;

    const clearRouteState = () => {
      navigate(".", { replace: true, state: null });
    };

    const promptIfDirty = async () => {
      const hasWorkspace = !!(currentId || currentName);
      if (!hasWorkspace) return "proceed";
      if (!isDirty) return "proceed";

      const res = await showDialog({
        title: "FP Structurer",
        message:
          "You have unsaved changes in the current fund. Continue and discard them?",
        buttons: [
          { label: "Cancel", value: "cancel", variant: "ghost" },
          { label: "Continue", value: "yes", variant: "primary" },
        ],
        defaultAction: "cancel",
      });

      return res === "yes" ? "proceed" : "cancel";
    };

    const seedNewUnsavedFromIssue = (seed) => {
      const nextSettlement = seed.settlementDate || today;
      const nextAmount = num(seed.totalAmount, 0) || 0;

      suppressDirtyRef.current = true;
      resetPlanner();

      setFundType(seed.fundType || "project");
      setFundMode("gross");
      setSelectedCurve("slgs");

      setSettlementDate(nextSettlement);
      setAsOfDate("");
      setTotalAmount(nextAmount);

      setLinkedDealId(seed.linkedDealId || null);
      setFundName("");

      setDrawScheduleFromHandoffSeed([]);
      setInvestmentSolution({});
      setSufficiencyTable([]);

      setCurrentId(null);
      setCurrentName("Linked Fund (from Issue)");
      setActiveTab("Main");

      suppressDirtyRef.current = false;
      setIsDirty(true);
    };

    const run = async () => {
      const okToProceed = await promptIfDirty();
      if (okToProceed !== "proceed") {
        clearRouteState();
        return;
      }

      if (!orgId) {
        await showDialog({
          title: "FP Structurer",
          message:
            "No organization is selected. Please select an organization, then retry the handoff from DebtStudio.",
          buttons: [{ label: "OK", value: "ok", variant: "primary" }],
          defaultAction: "ok",
        });
        clearRouteState();
        return;
      }

      if (payload.kind === "open" && payload.fundId) {
        try {
          const { data, error } = await supabase
            .from("fp_funds")
            .select(
              `
                id,
                org_id,
                tenant_id,
                name,
                created_by,
                updated_at,
                form,
                draw_schedule,
                solution,
                linked_ds_deal_id,
                fund_type,
                sharing
              `
            )
            .eq("id", payload.fundId)
            .single();

          if (error) throw error;
          handleLoad(data);
        } catch (e) {
          console.error("❌ [FS] open fund failed:", e);
          await showDialog({
            title: "FP Structurer",
            message: e?.message || "Failed to open the linked fund.",
            buttons: [{ label: "OK", value: "ok", variant: "primary" }],
            defaultAction: "ok",
          });
        } finally {
          clearRouteState();
        }
        return;
      }

      if (payload.kind === "handoff") {
        const dsDealId = payload.linkedDealId || null;

        const handoffFundType =
          typeof payload.fundType === "string" && payload.fundType.trim()
            ? payload.fundType.trim()
            : "project";

        if (!dsDealId) {
          await showDialog({
            title: "FP Structurer",
            message:
              "Missing linked deal id from DebtStudio handoff. Please save the issue and try again.",
            buttons: [{ label: "OK", value: "ok", variant: "primary" }],
            defaultAction: "ok",
          });
          clearRouteState();
          return;
        }

        try {
          const { data, error } = await supabase
            .from("fp_funds")
            .select(
              `
                id,
                org_id,
                tenant_id,
                name,
                created_by,
                updated_at,
                form,
                draw_schedule,
                solution,
                linked_ds_deal_id,
                fund_type,
                sharing
              `
            )
            .eq("org_id", orgId)
            .eq("linked_ds_deal_id", dsDealId)
            .eq("fund_type", handoffFundType)
            .maybeSingle();

          if (error) throw error;

          if (data?.id) {
            handleLoad(data);
            clearRouteState();
            return;
          }

          seedNewUnsavedFromIssue({
            linkedDealId: dsDealId,
            fundType: handoffFundType,
            settlementDate: payload.settlementDate || today,
            totalAmount: payload.totalAmount,
          });

          clearRouteState();
          return;
        } catch (e) {
          console.error("❌ [FS] linked fund lookup failed:", e);

          seedNewUnsavedFromIssue({
            linkedDealId: dsDealId,
            fundType: handoffFundType,
            settlementDate: payload.settlementDate || today,
            totalAmount: payload.totalAmount,
          });

          await showDialog({
            title: "FP Structurer",
            message:
              "Could not check for an existing linked fund. A new unsaved workspace has been created from the issue.",
            buttons: [{ label: "OK", value: "ok", variant: "primary" }],
            defaultAction: "ok",
          });

          clearRouteState();
          return;
        }
      }

      clearRouteState();
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location?.state,
    navigate,
    showDialog,
    isDirty,
    currentId,
    currentName,
    handleLoad,
    resetPlanner,
    setActiveTab,
    setAsOfDate,
    setCurrentId,
    setCurrentName,
    setFundMode,
    setFundName,
    setFundType,
    setInvestmentSolution,
    setIsDirty,
    setLinkedDealId,
    setSelectedCurve,
    setSettlementDate,
    setSufficiencyTable,
    setTotalAmount,
    suppressDirtyRef,
    today,
    orgId,
    setDrawScheduleFromHandoffSeed,
  ]);

  /* ------------------------------------------------------------------
   * Loading gate
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (
      hypotheticalConfig &&
      activeTab === "Cash Flow Sufficiency"
    ) {
      setActiveTab("Investment Solution");
    }
  }, [activeTab, hypotheticalConfig, setActiveTab]);

  if (profileLoading) {
    return (
      <Page title="FundPlanner - Structurer">
        <div className="p-6 text-muted">Loading user profile…</div>
      </Page>
    );
  }

  // Mirror DebtStudio behavior: workspace exists if we have a current record in context.
  const showWorkspace = !!(currentId || currentName);

  // FIX: object tabs, compatible with components/TopTabMenu
  const topTabs = [
    { key: "Main", label: "Main" },
    { key: "Cash Flow Schedule", label: "Requirements" },
    { key: "Investment Solution", label: "Investment Solution" },
    ...(hypotheticalConfig
      ? [{ key: "Stats", label: "Stats" }]
      : [
          { key: "Cash Flow Sufficiency", label: "Cash Flows" },
          { key: "Stats", label: "Stats" },
        ]),
  ];

  return (
    <Page
      title="FundPlanner - Structurer"
      showOrganizationSelector
      ribbon={
        <FileRibbon
          entityName="Fund"
          fileManager={{
            availableOrgs,
            selectedOrg,
            orgMode,
            selectOrg,
          }}
          onNew={handleNewFund}
          onSave={showWorkspace && isDirty ? handleSave : null}
          onSaveAs={currentId ? handleSaveAs : null}
          onOpenBrowse={showWorkspace ? () => setShowOpenModal(true) : null}
          onDelete={currentId && canDelete ? handleDeleteFund : null}
          currentName={currentName}
          currentOwner={currentOwner}
          animateSaving
          currentStatus={status}
          isReadOnly={isReadOnly}
        >
          {!showWorkspace ? (
            !selectedOrg ? (
              <div className="text-sm text-muted">
                Select an organization above to browse funds.
              </div>
            ) : (
              <FileManagerPanel
                entityName="Fund"
                adapter={fm}
                view={fundsOpenView}
                onSelect={(fund) => handleLoad(fund)}
                showToolbar
                showFooter
                maxHeightClassName="max-h-[55vh]"
              />
            )
          ) : null}
        </FileRibbon>
      }
    >
      {showWorkspace && (
        <>
          {showWizard && (
            <ModalBackdrop onClose={() => setShowWizard(false)}>
              <div
                className="bg-[hsl(var(--background))] text-app rounded-xl shadow-xl border border-border max-w-lg w-full mx-4 p-6"
                onClick={(e) => e.stopPropagation()}
              >
              <CashflowWizard
                mode={fundMode}
                settlementDate={settlementDate}
                totalAmount={totalAmount}
                setTotalAmount={setTotalAmount}
                onGenerate={handleWizardGenerate}
                onCancel={handleWizardCancel}
              />
              </div>
            </ModalBackdrop>
          )}

          <div className="rounded-2xl border border-border bg-app shadow-sm">
            <TopTabMenu
              tabs={topTabs}
              activeTab={activeTab}
              onChange={setActiveTab}
              actions={
                <button
                  type="button"
                  className="btn btn-primary whitespace-nowrap"
                  onClick={handleExportPdf}
                  disabled={
                    isSolving ||
                    isPdfGenerating ||
                    !structurerReport.hasResults
                  }
                  title={
                    structurerReport.hasResults
                      ? "Export the full FP Structurer report to PDF"
                      : hypotheticalConfig
                      ? "Calculate the fund before exporting a PDF"
                      : "Solve the fund before exporting a PDF"
                  }
                >
                  {isPdfGenerating ? "Generating PDF..." : "Export PDF"}
                </button>
              }
            />

            <div className="p-6 border-t border-border rounded-b-2xl">
              {activeTab === "Main" && (
                <FundSetup
                  fundType={fundType}
                  setFundType={setFundType}
                  fundMode={fundMode}
                  setFundMode={setFundMode}
                  securityType={selectedCurve}
                  setSecurityType={setSelectedCurve}
                  asOfDate={asOfDate}
                  setAsOfDate={setAsOfDate}
                  settlementDate={settlementDate || today}
                  setSettlementDate={setSettlementDate}
                  totalAmount={totalAmount}
                  setTotalAmount={setTotalAmount}
                  totalAmountLocked={totalAmountLocked}
                  onRunWizard={isReadOnly ? null : () => setShowWizard(true)}
                  fundName={fundName}
                  setFundName={setFundName}
                  linkedDealId={linkedDealId}
                  setLinkedDealId={setLinkedDealId}
                  selectedOrg={selectedOrg}
                  sharing={sharing}
                  setSharing={setSharing}
                  canChangeSharing={canChangeSharing}
                  isReadOnly={isReadOnly}
                />
              )}

              {activeTab === "Cash Flow Schedule" && (
                <DrawSchedule
                  drawRows={drawSchedule}
                  setDrawRows={setDrawScheduleFromGrid}
                  isSolving={isSolving}
                  onRunWizard={isReadOnly ? null : () => setShowWizard(true)}
                  fundType={fundType}
                  totalAmount={totalAmount}
                  setTotalAmount={setTotalAmount}
                  isReadOnly={isReadOnly}
                />
              )}

              {activeTab === "Investment Solution" && (
                <InvestmentSolution
                  rows={investmentSolution.investments || []}
                  solution={investmentSolution}
                  onSolve={handleSolve}
                  isSolving={isSolving}
                  fundMode={fundMode}
                  setFundMode={setFundMode}
                  securityType={selectedCurve}
                  setSecurityType={setSelectedCurve}
                  asOfDate={asOfDate}
                  setAsOfDate={setAsOfDate}
                  customCurveRows={customCurveRows}
                  setCustomCurveRows={setCustomCurveRows}
                  userCurveDate={userCurveDate}
                  setUserCurveDate={setUserCurveDate}
                  userCurveDescription={userCurveDescription}
                  setUserCurveDescription={setUserCurveDescription}
                  isReadOnly={isReadOnly}
                />
              )}

              {activeTab === "Cash Flow Sufficiency" && (
                <CashflowSufficiency
                  draws={drawSchedule}
                  allocations={investmentSolution.investments || []}
                  inflows={investmentSolution.inflows || []}
                  originalFunding={totalAmount}
                  settlementDate={settlementDate}
                  fundMode={fundMode}
                />
              )}

              {activeTab === "Stats" && (
                hypotheticalConfig ? (
                  <UsTreasuryStats
                    result={hypotheticalResult}
                    universeLabel={hypotheticalConfig.universeLabel}
                  />
                ) : (
                  <FundStats
                    allocations={investmentSolution.investments || []}
                    sufficiency={sufficiencyTable}
                    inflows={investmentSolution.inflows || []}
                  />
                )
              )}
            </div>
          </div>

        </>
      )}

      {showOpenModal && selectedOrg ? (
        <FileManagerModal
          entityName="Fund"
          adapter={fm}
          view={fundsOpenView}
          onClose={() => setShowOpenModal(false)}
          onSelect={(fund) => {
            handleLoad(fund);
            setShowOpenModal(false);
          }}
        />
      ) : null}
    </Page>
  );
}
